import './styles.scss';

import { useMutation } from '@apollo/client';
import { Loader } from '@ets-global/b2c-website-ui';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import PrepToolBanner from '../../../components/Base/Banner/PrepToolBanner';
import SessionSearchBanner from '../../../components/Base/Banner/SessionSearchBanner';
import { SmallText } from '../../../components/Base/Text';
import { useDateToString } from '../../../components/Element/DateToString';
import BuyingProcess from '../../../components/Layout/BuyingProcess';
import { CONFIRM_PURCHASE } from '../../../graphql/mutations';
import errorsHandler from '../../../helpers/errorsHandler';
import { getCookieConsent } from '../../../helpers/getCookieConsent';
import { useBasketCookie, useNotification, useRouter } from '../../../hooks';
import useDataLayer from '../../../hooks/useDataLayer';
import { paths } from '../../../paths';
import { PurchaseContext } from '../../../store/Purchase';
import ErrorDisplay from '../ErrorDisplay';

const Payment = () => {
  const history = useHistory();
  const { resetBasket } = useBasketCookie();
  const { newNotification } = useNotification();
  const [errorState, setErrorState] = useState({ hasError: false, errors: [] });
  const {
    lang,
    currentRoute: {
      params: { registrationId },
    },
    generatePath,
    generateUrl,
  } = useRouter();
  const { t } = useTranslation();
  const { pushEventLoadedPurchaseFunnel } = useDataLayer();
  const pageLoaded = useRef(false);

  const { purchase, loading, refetch } = useContext(PurchaseContext);
  const [confirm] = useMutation(CONFIRM_PURCHASE);
  const DateToString = useDateToString();
  const [cookies] = useCookies(['CookieConsent']);

  useEffect(() => {
    if (purchase && !pageLoaded.current) {
      pageLoaded.current = true;
      pushEventLoadedPurchaseFunnel({
        type: 'common',
        stepName: 'cart validation',
        stepNumber: 3,
      });

      onConfirm();
    }
  }, [purchase]);

  let banner = <PrepToolBanner />;

  const onConfirm = async () => {
    try {
      const {
        data: { confirmPurchase },
      } = await confirm({
        variables: {
          registrationId: registrationId,
          locale: lang,
          backUrl: generateUrl(paths.PURCHASE_PAYMENT_FINALISATION, {
            registrationId: purchase.registrationId,
            type: 'fail',
          }),
          cancelUrl: generateUrl(paths.PURCHASE_PAYMENT_FINALISATION, {
            registrationId: purchase.registrationId,
            type: 'fail',
          }),
          confirmationUrl: generateUrl(paths.PURCHASE_PAYMENT_FINALISATION, {
            registrationId: purchase.registrationId,
            type: 'success',
          }),
          initializePayment: purchase.total > 0,
          consent: getCookieConsent(cookies),
        },
      });

      if (!confirmPurchase || (purchase.total > 0 && !confirmPurchase.validationUrl)) {
        if (confirmPurchase.__typename === 'PurchaseCanNotBeConfirmed') {
          let parameters = [];

          if (confirmPurchase.parameters) {
            parameters = confirmPurchase.parameters.reduce((acc, parameter) => {
              if (parameter.purchasedAt !== undefined) {
                acc.purchasedAt = DateToString({
                  date: parameter.purchasedAt,
                  format: 'PPPp',
                });
              }

              return acc;
            }, {});
          }

          const errors = [];

          if (confirmPurchase.errorCodes) {
            confirmPurchase.errorCodes.forEach((errorCode) => {
              errors.push(t(`purchase-summary.error.${errorCode.toLowerCase()}`, parameters));
            });
          }

          if (confirmPurchase.errorList) {
            confirmPurchase.errorList.forEach((error) => {
              errors.push(t(`purchase-summary.error.${error.reason.toLowerCase()}`, parameters));
            });
          }

          if (confirmPurchase.errors) {
            confirmPurchase.errors.forEach((error) => {
              errors.push(t(`purchase-summary.error.${error.toLowerCase()}`));
            });
          }
          setErrorState({ hasError: true, errors });
        } else {
          throw new Error(t('purchase-summary.error.generic'));
        }
      }

      if (purchase.total === 0) {
        history.push({
          pathname: generatePath(paths.PURCHASE_PAYMENT_FINALISATION, {
            registrationId,
            type: 'success',
          }),
          search: `?orderID=E4-${purchase.id}`,
        });
      } else if (confirmPurchase.validationUrl) {
        window.location.replace(confirmPurchase.validationUrl);
      }
    } catch (error) {
      setErrorState({ hasError: true });
      errorsHandler({ error, newNotification, t });
    } finally {
      resetBasket();
      refetch();
    }
  };

  if (purchase && purchase.openSession && purchase.openSession.id) {
    banner = <SessionSearchBanner />;
  }

  const displayError = errorState.hasError || (!pageLoaded.current && !loading && (!registrationId || !purchase));

  return (
    <BuyingProcess>
      {banner}
      {displayError && (
        <ErrorDisplay errorTitle={<Trans>purchase-summary.purchase.invalid</Trans>} errors={errorState.errors} />
      )}

      {!displayError && !loading && (
        <div className="container container--padding">
          <SmallText>
            <Trans>payment.awaiting-message</Trans>
          </SmallText>
          <Loader />
        </div>
      )}
    </BuyingProcess>
  );
};

export default Payment;
